<script>
    
    import { onDestroy, onMount, tick } from "svelte";
    import { get } from "svelte/store";
    import Indicator from "./Indicator.svelte";
    import { Dispatcher } from "../../Dispatcher.js";
    import { Peers_Handler } from "../../Peers.js";
    import { OutputConfig } from "../../OutputConfig";

    $: unity_position = unity_ratio * level_slider_max;
    $: isMobile = OutputConfig.session.browser.isMobile;

    export let peer;
    export let controlsRendered;

    let render_level = false;
    let render_routing = false;

    let level = 0;
    let level_slider;
    let level_slider_max = 100; 
    const unity_ratio = 0.55; 
    let ro;

    const LEVEL_MIN_DB = -60;
    const LEVEL_MAX_DB = 18;
    let use_expanded_io = false;

    const unsub_expanded_io = Peers_Handler.self_peer?.use_expanded_io.subscribe(value => {
        use_expanded_io = value;
    });


    // -- mapping is linear above 0, then is exponential below that
    function dbToPixels(db) {
        if (db >= 0) return unity_position + (db * ((level_slider_max - unity_position) / 18));
        else return unity_position * (1 + (db / 60));
    }


    function pixelsToDb(position) {
        if (position >= unity_position) return (position - unity_position) * (18 / (level_slider_max - unity_position));
        else return -60 * (1 - (position / unity_position));
    }

    function handleVolumeChange(value) {
        
        // -- value is a pixel position
        if (!isMobile){

            const px_position = value.target.valueAsNumber;
            if (isNaN(px_position))
                return;

            level = pixelsToDb(px_position);
        }

        // -- or a db step
        else level += value;

        // -- validate the value
        if (typeof level !== "number" || isNaN(level))
            level = get(peer.render_level) ?? 0;

        // -- clamp it
        level = Math.min(LEVEL_MAX_DB, Math.max(LEVEL_MIN_DB, level));
        
        // -- notify and store
        Dispatcher.setTalentLevel(peer.id, level);
        peer.render_level.set(level);
    }


    function handleRoutingChange(event, selectorIndex) {
        
        const ch = parseInt(event.target.value);
        if (isNaN(ch))
            return;

        peer.render_routing[selectorIndex].set(ch - 1);

        Dispatcher.setTalentRouting(peer.id, {src_ch: selectorIndex, dest_ch: ch});
    }

    function decideRender(){
        
        if (peer.type === "talent" || (peer.type === "eng" && use_expanded_io)) render_level = true;            
        if (use_expanded_io && peer.type !== "client") render_routing = true;

        return (render_level || render_routing);

    }

    // --- maybe dont need
    function handleLevelResize() {
        if (level_slider) {
            const rect = level_slider.getBoundingClientRect();
            const h = Math.round(rect.height);
            level_slider_max = Math.max(h, 1);
            console.log("Level Slider Max", level_slider_max)
        }
    }

    onMount(async () => {
        
        controlsRendered(decideRender());
        level = get(peer.render_level);

        // -- Maybe wont need
        // Initialize slider metrics and observe for size changes
        // await tick();
        // handleLevelResize();
        // ro = new ResizeObserver(() => handleLevelResize());
        // if (level_slider) ro.observe(level_slider);
    })

    onDestroy(() => {
        ro && ro.disconnect();
        unsub_expanded_io && unsub_expanded_io();
    })

</script>

    {#if render_level}
        <div class="peer-controls" class:is-mobile={isMobile}>
            
            {#if !isMobile}
                <div class="level-wrapper">
                    <input 
                        type="range" 
                        min="0"
                        max={level_slider_max}
                        step="0.1"
                        class="level-slider"
                        value={dbToPixels(level)}
                        on:input={handleVolumeChange}
                        bind:this={level_slider}
                    />
                    <span class="db-value" class:is-mobile={isMobile}>{level > 0 ? '+' + level.toFixed(1) : level.toFixed(1)}</span>
                </div>
            {:else}
                <span class="db-value" class:is-mobile={isMobile}>{level > 0 ? '+' + level.toFixed(1) : level.toFixed(1)}</span>
                <i 
                    class="fa-sharp fa-thin fa-square-caret-up" 
                    aria-label="Increase volume"
                    on:click={() => handleVolumeChange(1)}
                ></i>
                <i 
                    class="fa-sharp fa-thin fa-square-caret-down" 
                    aria-label="Decrease volume"
                    on:click={() => handleVolumeChange(-1)}
                ></i>
            {/if}
            
           
            
        </div>    
    {/if}

    {#if render_routing}
        <div class="routing-container">
            {#each Array(4) as _, i}
                <div class="routing-group">
                    <Indicator meterSource="stream-ch" peerType={peer.type} peerId={peer.id} channelNum={i} compact={true}/>
                    <select 
                        class="number-selector"
                        on:change={(e) => handleRoutingChange(e, i)}
                    >
                        {#each Array(4) as _, j}
                            <option value={j + 1} selected={get(peer.render_routing[i]) === j}>{j + 1}</option>
                        {/each}
                    </select>
                </div>
            {/each}
        </div>
    {/if}

<style lang="scss">

    @import "../styles/main.scss";

    .peer-controls {
        background: hsla(0, 0%, 0%, 0.32);        
        &.is-mobile {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
			font-size: 30px;
            gap: 3px;
            color: #be9d86;
		}
    
    }

    // box-shadow: 0 5px 10px rgba(154,160,185,.05), 0 15px 40px rgba(166,173,201,.2);
    .fa-square-caret-up {
        // margin-bottom: 5px;
        cursor: pointer;
    }

    .fa-square-caret-down {
        // margin-top: 5px;
        cursor: pointer;
    }

    // -- Level --
    .level-wrapper {
                
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        width: 100%;
        height: 100%;
    }

    .level-slider {
    
        writing-mode: vertical-lr;
        transform: rotate(180deg);
        
        border-radius: 2px;
        background: $light-color;
        opacity: 0.7;
        transition: opacity 0.2s;
        
        accent-color: #eee;

        &:hover {
            opacity: 1;
        }

        &::-webkit-slider-thumb {
            
            -webkit-appearance: none;
            appearance: none;
            
            width: 12px;
            height: 12px;
            
            border-radius: 50%;
            background: $light-color;
            
            cursor: pointer;
            transition: transform 0.1s;

            &:hover {
                transform: scale(1.2);
            }
        }

        &::-moz-range-thumb {
            
            width: 12px;
            height: 12px;

            border-radius: 50%;
            background: $light-color;
            
            cursor: pointer;
            transition: transform 0.1s;
            border: none;

            &:hover {
                transform: scale(1.2);
            }
        }
    }

    .db-value {
        color: $light-color;
        font-size: $font-xsm;
        text-align: center;
        min-height: 1.2em;
        margin-top: 3px;
        &.is-mobile {
            margin-top: 0px;
            margin-bottom: 3px;
        }
    }



    // -- Routing --
    .routing-group {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .routing-container {
        grid-column: 1 / -1;
        grid-row: 2;
        display: flex;
        gap: $space-sm;
        justify-content: center;
        background: hsla(0, 0%, 0%, 0.32);
        border-radius: 16px;
        margin-top: $space-sm;
        width: fit-content;
        margin-right: 3px;
        
        // margin-left: auto;
        // padding: $space-sm;
    }

    // .peer-controls .routing-container {
    //     flex-direction: column;
    //     width: 100%;
    //     margin-left: 0;
    //     margin-right: 0;
    // }

    .number-selector {
        background: transparent;
        color: $light-color;
        border: 1px solid #faebd775;
        border-radius: 4px;
        padding: 2px 4px;
        font-size: $font-xsm;
        width: 35px;
        opacity: 0.7;
        transition: opacity 0.2s;
        cursor: pointer;
        text-align: center;

        &:hover {
            opacity: 1;
        }

        option {
            background: #2a2a2a;
            text-align: center;
        }
    }


</style>